import { REQUEST_TYPE_LABELS, type RequestDetails, type RequestType, requestStatusLabel } from '@erp/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { Comments } from '@/components/comments';
import { FileList } from '@/components/file-upload';
import { EntityStatus, RoutePanel } from '@/components/process';
import { ErrorBox, Spinner } from '@/components/ui';
import { formatDay, formatMoment, formatMoney } from '@/lib/format';
import { requestQuery } from '@/lib/queries';
import { REQUEST_ICONS } from './requests';
import { CancelBlock, Row } from './vacation';

const str = (v: unknown) => (typeof v === 'string' && v ? v : null);

/** Поля заявки по виду — в том порядке, как их заполняли. */
function dataRows(r: RequestDetails): [string, ReactNode][] {
  const d = r.data;
  const rows: [string, ReactNode | null][] = [];
  const money = r.amount !== null ? formatMoney(r.amount, r.currency) : null;
  switch (r.type as RequestType) {
    case 'business_trip':
      rows.push(
        ['Куда', str(d.destination)],
        ['Даты', `${formatDay(str(d.startDate))} — ${formatDay(str(d.endDate))}`],
        ['Цель поездки', str(d.purpose)],
      );
      break;
    case 'invoice':
      rows.push(
        ['За что платим', str(d.purpose)],
        ['Поставщик', str(d.supplier)],
        ['Сумма', money],
        [
          'Счёт',
          [
            str(d.invoiceNumber) && `№ ${d.invoiceNumber}`,
            str(d.invoiceDate) && `от ${formatDay(str(d.invoiceDate))}`,
          ]
            .filter(Boolean)
            .join(' ') || null,
        ],
      );
      break;
    case 'cash':
      rows.push(
        ['Назначение', str(d.purpose)],
        ['Сумма', money],
        ['Нужны к дате', str(d.neededBy) && formatDay(str(d.neededBy))],
      );
      break;
    case 'outgoing':
      rows.push(
        ['Кому', str(d.recipient)],
        ['Номер документа', str(d.documentNumber)],
        ['Дата документа', formatDay(str(d.documentDate))],
        ['О документе', str(d.about)],
      );
      break;
  }
  return rows.filter((row): row is [string, ReactNode] => Boolean(row[1]));
}

export function RequestPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useQuery(requestQuery(id));
  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const r = q.data;
  const Icon = REQUEST_ICONS[r.type];

  return (
    <div className="max-w-[1180px]">
      <Link
        to="/requests"
        className="mb-4 inline-flex items-center gap-1 text-[13px] text-steel hover:text-ink"
      >
        <ChevronLeft className="size-4" aria-hidden />
        Заявки
      </Link>
      <header className="mb-6">
        <p className="flex items-center gap-1.5 text-[13px] tabular-nums text-steel">
          <Icon className="size-4" aria-hidden />
          {REQUEST_TYPE_LABELS[r.type]} №{r.number}
        </p>
        <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.015em] break-words">{r.title}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <EntityStatus status={r.status} label={requestStatusLabel(r.type, r.status)} />
          {r.amount !== null && (
            <span className="text-[16px] font-semibold tabular-nums">
              {formatMoney(r.amount, r.currency)}
            </span>
          )}
        </div>
      </header>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-6">
          {r.route && <RoutePanel route={r.route} />}
          <Comments entityType="request" entityId={r.id} />
        </div>
        <aside className="flex flex-col gap-6">
          <section className="panel px-5 py-4">
            <dl className="flex flex-col gap-3.5">
              {dataRows(r).map(([label, value]) => (
                <Row key={label} label={label}>
                  <span className="whitespace-pre-wrap">{value}</span>
                </Row>
              ))}
              <Row label="Автор">{r.author.fullName}</Row>
              <Row label="Подана">{formatMoment(r.createdAt)}</Row>
            </dl>
          </section>
          {r.files.length > 0 && (
            <section className="panel px-5 py-4">
              <h2 className="mb-2 font-semibold">Файлы</h2>
              <FileList files={r.files} />
            </section>
          )}
          {r.permissions.canCancel && (
            <CancelBlock
              url={`/requests/${r.id}/cancel`}
              question="Отменить заявку?"
              hint="Маршрут остановится, открытые задания снимутся."
              label="Отменить заявку"
            />
          )}
        </aside>
      </div>
    </div>
  );
}
